<template lang="pug">
VBtn(
  :color="onList ? 'secondary' : undefined"
  :aria-label="buttonTitle"
  :title="buttonTitle"
  :icon="true"
  size="small"
  variant="text"
  @click.stop.prevent="onList = !onList"
)
  VIcon {{ onList ? mdiStar : mdiStarOutline }}
</template>

<script setup lang="ts">
import { computed } from "vue";

import { mdiStar, mdiStarOutline } from "@mdi/js";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import {
  defaultListName,
  defaultListTitleEn,
  defaultListTitleKey,
  useReadingListsStore,
} from "~/stores/useReadingListsStore";
import { useToastStore } from "~/stores/useToastStore";
import { getListViewLocation } from "~/routing/locations";

const i18n = useI18n();
const readingListsStore = useReadingListsStore();
const toastStore = useToastStore();
const router = useRouter();
const localePath = useLocalePath();

interface Props {
  /**
   * The comment ID.
   */
  nid: number;

  listName?: string;

  listTitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  listName: defaultListName,
  listTitle: defaultListTitleEn,
});

const listTitle = computed<string>(() =>
  props.listName === defaultListName
    ? i18n.t(defaultListTitleKey)
    : props.listTitle,
);

const onList = computed<boolean>({
  get() {
    return readingListsStore.listContainsItem(props.listName, props.nid);
  },
  set(newValue) {
    if (newValue) {
      readingListsStore.addToList(props.listName, [
        {
          commentId: props.nid,
          addedDate: Date.now(),
        },
      ]);

      toastStore.toast({
        text: i18n.t("ReadingList.AddedToThisReadingList", {
          title: listTitle.value,
        }),
        actions: [
          {
            text: i18n.t("ReadingList.Go"),
            onClick: () => {
              router.push(localePath(getListViewLocation(props.listName)));
            },
          },
        ],
      });
    } else {
      readingListsStore.removeFromList(props.listName, [props.nid]);

      toastStore.toast({
        text: i18n.t("ReadingList.RemovedFromThisReadingList", {
          title: listTitle.value,
        }),
        actions: [],
      });
    }
  },
});

const buttonTitle = computed<string>(() => {
  return onList.value
    ? i18n.t("ReadingList.RemoveFromThisList", { title: listTitle.value })
    : i18n.t("ReadingList.AddToThisList", { title: listTitle.value });
});
</script>
